import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';
import CharacterCounter from '../components/CharacterCounter';
import Footer from '../components/Footer';
import { loadState, updatePage } from '../utils/stateManager';
import { extractIdentity, generateStorageAccountName } from '../utils/bicepGenerator';
import './Page5StorageAccount.css';

function Page5StorageAccount() {
  const navigate = useNavigate();
  const [useStorageAccount, setUseStorageAccount] = useState(false);
  const [environment, setEnvironment] = useState('production');
  const [saName, setSaName] = useState('');
  const [saValid, setSaValid] = useState(true);
  const [saErrors, setSaErrors] = useState([]);
  const [subscription, setSubscription] = useState('');

  useEffect(() => {
    const state = loadState();
    if (state.page5StorageAccount !== undefined) {
      setUseStorageAccount(state.page5StorageAccount.useStorageAccount || false);
    }
    if (state.page3VMConfig) {
      setEnvironment(state.page3VMConfig.environment || 'production');
      // Auto-check for production
      if (state.page3VMConfig.environment === 'production' && state.page5StorageAccount === undefined) {
        setUseStorageAccount(true);
      }
    }
    const sub = state.page2Discovery?.subscription || '';
    setSubscription(sub);
    if (sub) {
      const { front9, back9 } = extractIdentity(sub);
      const result = generateStorageAccountName(front9, back9, 'sa_boot_diagnostics');
      setSaName(result.name);
      setSaValid(result.isValid);
      setSaErrors(result.errors);
    }
  }, []);

  const handleToggle = (checked) => {
    setUseStorageAccount(checked);
    updatePage('page5StorageAccount', { useStorageAccount: checked });
  };

  const handleBack = () => {
    navigate('/page4');
  };

  const handleContinue = () => {
    navigate('/page6');
  };

  const isProduction = environment === 'production';
  const isNonProd = ['development', 'testing', 'staging'].includes(environment);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Azure VM Deployment Wizard</h1>
        <ProgressBar currentPage={5} totalPages={9} />
      </div>

      <div className="page-content">
        <div className="section-header">
          <h2>Step 5: Storage Account - Boot Diagnostics</h2>
          <span className="badge-optional">OPTIONAL</span>
        </div>

        <div className="what-it-does">
          <h3>📦 What It Does</h3>
          <ul>
            <li>Stores VM boot logs and screenshots</li>
            <li>Critical for troubleshooting boot failures</li>
            <li>Required for serial console access</li>
            <li>Helps diagnose startup and configuration issues</li>
          </ul>
        </div>

        <div className="cost-section">
          <h3>💰 Cost</h3>
          <p className="cost-value">~$2-5/month</p>
          <p className="cost-note">(Minimal storage costs for diagnostics data)</p>
        </div>

        {isProduction && (
          <div className="recommendation production">
            <h3>✅ Recommended for Production</h3>
            <p>
              <strong>Enable this for production VMs.</strong> Boot diagnostics are essential
              for troubleshooting production issues quickly. The cost is minimal compared to
              the value when diagnosing critical VM failures.
            </p>
          </div>
        )}

        {isNonProd && (
          <div className="recommendation non-prod">
            <h3>💡 Recommendation for Non-Production</h3>
            <p>
              <strong>Consider skipping this for dev/test/staging.</strong> Azure provides
              free managed boot diagnostics that are sufficient for non-production workloads.
              A dedicated storage account is typically not needed unless you require longer
              retention or specific compliance requirements.
            </p>
          </div>
        )}

        <div className="alternatives">
          <h3>🔄 Alternative: Azure-Managed Boot Diagnostics</h3>
          <p>
            Azure offers free, managed boot diagnostics (without a storage account) that work
            for most scenarios. The managed option:
          </p>
          <ul>
            <li>✓ No additional cost</li>
            <li>✓ Automatic management (no storage account to maintain)</li>
            <li>✓ Sufficient for most troubleshooting needs</li>
            <li>✗ Limited retention (~7 days)</li>
            <li>✗ Cannot customize storage or access patterns</li>
          </ul>
        </div>

        <div className="decision-section">
          <h3>Your Decision</h3>
          <div className="checkbox-container">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={useStorageAccount}
                onChange={(e) => handleToggle(e.target.checked)}
              />
              <span className="checkbox-text">
                Create Storage Account for Boot Diagnostics
              </span>
            </label>
          </div>

          {useStorageAccount && (
            <div className="selection-info">
              {saName ? (
                <>
                  <div className="resource-card">
                    <div className="resource-header">
                      <span className={`status-icon ${saValid ? 'valid' : 'invalid'}`}>
                        {saValid ? '✅' : '❌'}
                      </span>
                      <strong>Your Storage Account Name:</strong>
                    </div>
                    <CharacterCounter value={saName} maxLength={24} />
                    {!saValid && saErrors.length > 0 && (
                      <p className="error-message">{saErrors.join(', ')}</p>
                    )}
                  </div>
                  <p className="naming-example">
                    Pattern: <code>sa11&#123;f9&#125;&#123;b9&#125;</code> (lowercase, 24 chars max)<br />
                    From subscription: "<em>{subscription}</em>"
                  </p>
                </>
              ) : (
                <p>
                  ✅ A storage account will be created with the naming pattern:
                  <code>sa11&#123;f9&#125;&#123;b9&#125;</code> (go back to Step 2 to enter your subscription name)
                </p>
              )}
            </div>
          )}

          {!useStorageAccount && (
            <div className="selection-info">
              <p>
                ℹ️ Azure-managed boot diagnostics (free) will be used instead.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="page-actions">
        <button className="btn-secondary" onClick={handleBack}>
          ← Back
        </button>
        <button className="btn-primary" onClick={handleContinue}>
          Next: Log Analytics →
        </button>
      </div>

      <Footer />
    </div>
  );
}

export default Page5StorageAccount;
